import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { SessionTimer } from "@/components/SessionTimer";
import { Button } from "@/components/ui/button";
import { SignOut, ShieldStar, Receipt } from "@phosphor-icons/react";

export function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <header
      className="sticky top-0 z-50 border-b border-zinc-200 bg-white/85 backdrop-blur-xl saturate-150"
      data-testid="app-header"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        <Link to="/pricelist" className="flex items-center">
          <BrandLogo />
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          <NavLink
            to="/pricelist"
            data-testid="nav-pricelist"
            className={({ isActive }) =>
              `px-4 py-2 text-sm font-semibold tracking-wide transition-colors rounded-md ${
                isActive ? "text-brand bg-brand-soft" : "text-zinc-600 hover:text-brand hover:bg-brand-soft/60"
              }`
            }
          >
            Pricelist
          </NavLink>
          <NavLink
            to="/quotes"
            data-testid="nav-quotes"
            className={({ isActive }) =>
              `px-4 py-2 text-sm font-semibold tracking-wide transition-colors rounded-md inline-flex items-center gap-1.5 ${
                isActive ? "text-brand bg-brand-soft" : "text-zinc-600 hover:text-brand hover:bg-brand-soft/60"
              }`
            }
          >
            <Receipt size={16} weight="duotone" /> Quotes
          </NavLink>
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-semibold tracking-wide transition-colors rounded-md inline-flex items-center gap-1.5 ${
                  isActive ? "text-brand bg-brand-soft" : "text-zinc-600 hover:text-brand hover:bg-brand-soft/60"
                }`
              }
            >
              <ShieldStar size={16} weight="duotone" />
              Admin Console
            </NavLink>
          )}
        </nav>

        <div className="flex-1" />

        <div className="flex items-center gap-3">
          <SessionTimer />
          {user && (
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-[10px] text-zinc-500 uppercase tracking-widest font-semibold">{user.role}</span>
              <span className="text-sm text-zinc-800 font-semibold" data-testid="header-user-email">
                {user.email}
              </span>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="border-zinc-300 bg-white hover:bg-brand-soft hover:text-brand hover:border-brand text-zinc-700 gap-2"
          >
            <SignOut size={16} weight="bold" />
            Logout
          </Button>
        </div>
      </div>
    </header>
  );
}
